import React, { useState, useMemo, useCallback } from 'react';
import defaultTo from 'lodash/defaultTo';
import uniq from 'lodash/uniq';
import compact from 'lodash/compact';
import { ApolloQueryResult, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { createInvoiceGroup } from '@atlassian/cssxp-proxies';
import { ProxyGraphClients, abgGraphqlClient } from '@atlassian/temp-client-utils';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { createContactRelationship } from '@atlassian/temp-proxies';
import { CreateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import { ProgressTracker, Stages } from '@atlaskit/progress-tracker';
import Button, { LoadingButton } from '@atlaskit/button';
import { ProgressIndicator } from '@atlaskit/progress-indicator';
import Form, { Field, FormFooter, FormHeader, FormSection, HelperMessage, RequiredAsterisk } from '@atlaskit/form';
import ButtonGroup from '@atlaskit/button/button-group';
import { AddContactFields, AddContactSelectFields } from '@css-xp/ppc-add-contact-fields';
import { useApplicationContext } from '@css-xp/base/common-context';
import { CreateContactRelationshipsRequest } from '@css-xp/contact-resources/src/constant';
import {
  useFormSubmit,
  useNotification,
  ErrorComponents,
  InfoPanel,
  useSidebar,
  successNotificationBuilder,
  NotificationTypes,
  useFeatureToggle,
  ContactsTooltip,
  ContactsTooltipType,
} from '@css-xp/base';
import { useContactServicePayload } from '@css-xp/contact-resources';
import {
  CommerceAddress,
  FetchTransactionAccountForCreateBillingProfileQuery,
  FetchTransactionAccountForCreateBillingProfileQueryVariables,
} from '@css-xp/graphtypes/src/generated-types-new';
import { withApollo } from '@css-xp/base/graphql-provider';
import { HelpContent, getCurrencyByCountry } from '@css-xp/ppc-common';
import {
  BillingAdminEdge,
  FetchBillingAdminsForTransactionAccountPpcQuery,
  FetchBillingAdminsForTransactionAccountPpcQueryVariables,
} from '@css-xp/ppc-graphtypes';
import { getMaxLimitOfCustomerContact, getRouteUrl } from '@css-xp/ppc-utils';
import { STATSIG_FEATURE_GATES, routePaths } from '@css-xp/ppc-constants';
import {
  SoldToAddressStep,
  CurrencyPONumberStep,
  PaymentMethodStep,
  FormData,
} from '../components/billing-profile-components';
import { fetchBillingAdminsForTransactionAccountPPC } from '../../../../queries';
import { fetchTransactionAccountForCreateBillingProfile } from '../queries/fetch-transaction-account-for-create-billing-profile';
import './create-billing-profile.scss';

enum FormStages {
  SOLD_TO_ADDRESS_STEP = 'Sold-to address',
  CUSTOMER_CONTACT_STEP = 'Customer contacts',
  BILLING_CONTACT_STEP = 'Billing admins',
  PAYMENT_METHOD_STEP = 'Payment method',
  CURRENCY_AND_PO_NUMBER_STEP = 'Currency & PO number',
}

enum TrackerState {
  VISITED = 'visited',
  CURRENT = 'current',
  UNVISITED = 'unvisited',
}

export enum FormTrackerSteps {
  SOLD_TO_ADDRESS_STEP = 'SOLD_TO_ADDRESS_STEP',
  CUSTOMER_CONTACT_STEP = 'CUSTOMER_CONTACT_STEP',
  BILLING_CONTACT_STEP = 'BILLING_CONTACT_STEP',
  PAYMENT_METHOD_STEP = 'PAYMENT_METHOD_STEP',
  CURRENCY_AND_PO_NUMBER_STEP = 'CURRENCY_AND_PO_NUMBER_STEP',
}

let stages = Object.values(FormStages);

// transforming enum to array for adding and removing progress step counts runtime based on FF state
let defaultFormTrackerStep = Object.values(FormTrackerSteps);

export const isValidAddress = (billingAddress: CommerceAddress | null | undefined): boolean => {
  if (
    (billingAddress?.name ||
      billingAddress?.addressLine1 ||
      billingAddress?.state ||
      billingAddress?.city ||
      billingAddress?.postcode) &&
    !(
      billingAddress?.name &&
      billingAddress?.addressLine1 &&
      billingAddress?.state &&
      billingAddress?.city &&
      billingAddress?.postcode
    )
  ) {
    return false;
  }
  return true;
};

export function getError(billingProfileData: FormData) {
  const soldToAddress = billingProfileData.soldToAddress;
  const missingFields: string[] = [];
  if (!soldToAddress?.name) missingFields.push('Company Name');
  if (!soldToAddress?.addressLine1) missingFields.push('Address 1');
  if (!soldToAddress?.addressLine2) missingFields.push('Country');
  if (!soldToAddress?.state) missingFields.push('State');
  if (!soldToAddress?.city) missingFields.push('City');
  if (!soldToAddress?.postcode) missingFields.push('Postal Code');

  const errorMsg = missingFields.length
    ? missingFields.join(', ') + `${missingFields.length > 1 ? ' are' : ' is'} missing`
    : '';
  return errorMsg;
}

export const CreateBillingProfilePane = withApollo(
  ({
    transactionAccountId,
    transactionAccountDetails,
    refetch,
    soldToAddresses,
    defaultSoldToAddress,
    isDefaultAddressSelected,
    isProgressIndicatorEnabled,
    onSubmit,
    addCustomerFormStages,
    initialCurrentProgressIndex,
    isSubmitLoading,
    isTermPaymentSelectionDisabled,
    onBackCustomerClick,
  }: {
    transactionAccountId: string;
    transactionAccountDetails?: any;
    refetch?: () => Promise<ApolloQueryResult<unknown>>;
    soldToAddresses?: CommerceAddress[];
    defaultSoldToAddress?: CommerceAddress;
    isDefaultAddressSelected?: boolean;
    isProgressIndicatorEnabled?: boolean;
    onSubmit?: (_requestForm: CreateInvoiceGroupRequest, _contactServicePayload: any) => void;
    addCustomerFormStages?: string[];
    initialCurrentProgressIndex?: number;
    isSubmitLoading?: boolean;
    isTermPaymentSelectionDisabled?: boolean;
    onBackCustomerClick?: () => void;
  }) => {
    const [isEnabled] = useFeatureToggle();
    const partnerData = useApplicationContext();
    const selectedPartnerAccountData = partnerData?.partnerAccounts.find(
      ({ transactionAccountId: txaId }: { transactionAccountId: string }) => txaId === transactionAccountId
    );

    if (initialCurrentProgressIndex === 1) {
      defaultFormTrackerStep = addCustomerFormStages as FormTrackerSteps[];
      stages = addCustomerFormStages as FormStages[];
    }

    const [isPOMultipleUseChecked, setIsPOMultipleUseChecked] = useState(false);
    const [shipToAddress, setShipToAddress] = useState<CommerceAddress | undefined | null>(defaultSoldToAddress);
    const [currentProgressIndex, setCurrentProgressIndex] = useState<number>(initialCurrentProgressIndex || 0);
    const [billingProfileData, setBillingProfileData] = useState<FormData>({});
    const [isNotValidAddress, setIsNotValidAddress] = useState<boolean>(false);

    const [primaryCustomerContactAccountIds, setPrimaryCustomerContactAccountIds] = useState<Array<string>>([]);
    const [primaryCustomerContactAccountEmails, setPrimaryCustomerContactAccountEmails] = useState<Array<string>>(['']);
    const [secondaryCustomerContactAccountIds, setSecondaryCustomerContactAccountIds] = useState<Array<string>>([]);
    const [secondaryCustomerContactAccountEmails, setSecondaryCustomerContactAccountEmails] = useState<Array<string>>([
      '',
    ]);
    const [billingContactAtlassianAccountIds, setBillingContactAtlassianAccountIds] = useState<Array<string>>(['']);
    const [billingContactAtlassianAccountEmails, setBillingContactAtlassianAccountEmails] = useState<Array<string>>([
      '',
    ]);
    const isBillingProfileNotificationEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_NOTIFICATION_AT_BILLING_PROFILE);
    const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
    const isPaypalSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
    const isCustomerContactV3Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);

    const { getCreateContactRelationshipsPayload } = useContactServicePayload();
    const ppcBffGraphqlClient = React.useMemo(() => abgGraphqlClient(ProxyGraphClients.PPC_BFF), []);

    let transactionAccountInfo = transactionAccountDetails;
    const { data: transactionAccountData, loading: transactionAccountDataLoading } = useQuery<
      FetchTransactionAccountForCreateBillingProfileQuery,
      FetchTransactionAccountForCreateBillingProfileQueryVariables
    >(fetchTransactionAccountForCreateBillingProfile, {
      variables: { id: transactionAccountId },
      skip: transactionAccountDetails,
    });

    if (transactionAccountData) {
      transactionAccountInfo = transactionAccountData;
    }
    const availablePaymentMethods = transactionAccountInfo?.transactionAccount?.availablePaymentMethods?.nodes;
    const termPaymentStatus = transactionAccountInfo?.transactionAccount?.billingAccountsList?.status;

    const { data: billingAdminsData, loading: billingAdminsLoading } = useQuery<
      FetchBillingAdminsForTransactionAccountPpcQuery,
      FetchBillingAdminsForTransactionAccountPpcQueryVariables
    >(fetchBillingAdminsForTransactionAccountPPC, {
      variables: {
        id: transactionAccountId,
        billingAdminOptions: {
          partnerAccountId: defaultTo(selectedPartnerAccountData?.id, ''),
          billingAdminIds: [],
        },
      },
      client: ppcBffGraphqlClient,
      displayName: `fetchBillingAdmins`,
      notifyOnNetworkStatusChange: true,
      nextFetchPolicy: 'cache-first',
      skip: !isBillingProfileNotificationEnabled || !selectedPartnerAccountData?.id,
    });

    const billingAdminConnection = billingAdminsData?.transactionAccount?.billingAdmins;
    const txaBillingAdmins = billingAdminConnection?.edges || [];

    const { closeSidebar } = useSidebar();

    const isPONumberFeatureEnabled = false;

    if (!isPONumberFeatureEnabled) {
      stages = stages.filter((stage) => stage !== FormStages.CURRENCY_AND_PO_NUMBER_STEP);
      defaultFormTrackerStep = defaultFormTrackerStep.filter(
        (step) => step !== FormTrackerSteps.CURRENCY_AND_PO_NUMBER_STEP
      );
    }

    if (!isBillingProfileNotificationEnabled) {
      stages = stages.filter((stage) => stage !== FormStages.BILLING_CONTACT_STEP);
      defaultFormTrackerStep = defaultFormTrackerStep.filter((step) => step !== FormTrackerSteps.BILLING_CONTACT_STEP);
    }

    const FormTrackerStep = defaultFormTrackerStep.reduce((acc, step, index) => {
      acc[step] = index;
      return acc;
    }, {} as Record<string, number>);

    const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();

    const { error, isProcessing, submitForm, complete, data } = useFormSubmit<CreateInvoiceGroupRequest, any>({
      submitFormAction: createInvoiceGroup,
    });

    const maxLimitOfCustomerContact = getMaxLimitOfCustomerContact();
    const isAddNewSecondaryContactDisabled = secondaryCustomerContactAccountIds?.length >= maxLimitOfCustomerContact;

    React.useEffect(() => {
      if (data) {
        const createPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: data?.id,
          transactionAccountId: transactionAccountId,
          createContactIds: uniq(compact([...primaryCustomerContactAccountIds, ...secondaryCustomerContactAccountIds])),
          ...(isCustomerContactV3Enabled && { primaryContactId: primaryCustomerContactAccountIds[0] }),
          createBillingContactIds: isBillingProfileNotificationEnabled ? billingContactAtlassianAccountIds : [],
        });
        contactServiceHandler(createPayload as CreateContactRelationshipsRequest);
      }
    }, [data]);

    const contactServiceHandler = async (contactPayLoad: CreateContactRelationshipsRequest) => {
      await createContactRelationship(contactPayLoad);
    };

    const submitCreateInvoiceGroupForm = useCallback(
      (formData: FormData) => {
        setIsNotValidAddress(false);
        const finalFormData = { ...billingProfileData, ...formData };
        setBillingProfileData(finalFormData);
        if (
          currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP &&
          !isValidAddress(finalFormData?.soldToAddress)
        ) {
          setIsNotValidAddress(true);
          return;
        }

        if (currentProgressIndex < stages.length - 1) {
          setCurrentProgressIndex(currentProgressIndex + 1);
          return;
        }

        const invoiceContacts = [];
        for (const key in finalFormData) {
          // REMOVAL OF (-) FROM RECIPIENTS
          if (key.includes('recipients')) {
            // @ts-ignore
            invoiceContacts.push(finalFormData[key]);
          }
        }

        const requestForm: CreateInvoiceGroupRequest = {
          transactionAccountId: transactionAccountId,
          data: {
            name: defaultTo(finalFormData.invoiceGroupName, ''),
            currency: isPONumberFeatureEnabled
              ? defaultTo(finalFormData.currency, '')
              : getCurrencyByCountry(shipToAddress?.country),
            ...(isCustomerContactV3Enabled
              ? {}
              : { recipients: invoiceContacts.filter((recipient: string) => recipient.length > 0) }),
          },
        };

        if (finalFormData.defaultPaymentMethod) {
          requestForm.data = {
            ...requestForm.data,
            defaultPaymentMethod: finalFormData.defaultPaymentMethod,
          };
        }

        // check if we selected an existing shipTo and not made any updates to it
        if (shipToAddress && shipToAddress.id) {
          // send existing shipToPartyId to update it
          requestForm.data.shipToPartyId = shipToAddress?.id;
        }
        if (finalFormData.soldToAddress === null) {
          requestForm.data.shipToPartyId = undefined;
        }

        if (isPONumberFeatureEnabled && finalFormData.purchaseOrderNumber) {
          requestForm.data.purchaseOrder = {
            number: finalFormData.purchaseOrderNumber,
            oneTimeUse: !isPOMultipleUseChecked,
          };
        }
        if (onSubmit) {
          const contactServicePayload = {
            transactionAccountId: transactionAccountId,
            createContactIds: uniq(
              compact([...primaryCustomerContactAccountIds, ...secondaryCustomerContactAccountIds])
            ),
            ...(isCustomerContactV3Enabled && { primaryContactId: primaryCustomerContactAccountIds[0] }),
            createBillingContactIds: isBillingProfileNotificationEnabled ? billingContactAtlassianAccountIds : [],
          };
          onSubmit(requestForm, contactServicePayload);
        } else {
          submitForm(requestForm);
        }
      },
      [
        billingProfileData,
        currentProgressIndex,
        isPONumberFeatureEnabled,
        shipToAddress,
        submitForm,
        transactionAccountId,
        isPOMultipleUseChecked,
        onSubmit,
      ]
    );

    const getBillingProfileUrl = (name: string, id: string) => {
      return (
        <Link
          to={`${getRouteUrl(routePaths.billingProfileDetails, {
            txaId: transactionAccountId,
            billingProfileId: id,
          })}`}
          onClick={(e) => e.stopPropagation()}
        >
          {name}
        </Link>
      );
    };

    if (complete && !notificationSent) {
      const successMessage = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('create-invoice-group-success')
        .withHeader(<>Created billing profile {getBillingProfileUrl(data?.name, data?.id)}.</>)
        .withDismissAfter(5000)
        .build();
      sendSuccessNotification(successMessage);
      setNotificationSent(true);
      closeSidebar();
      if (refetch) {
        refetch();
      }
    }

    const onPOMultipleUseChangeEvent = () => {
      setIsPOMultipleUseChecked((current) => !current);
    };

    const getFormStep = useCallback(
      (formProps: {
        disabled: boolean;
        getValues: () => FormData;
        setFieldValue: (_name: string, _value: any) => void;
      }) => {
        switch (currentProgressIndex) {
          case FormTrackerStep.SOLD_TO_ADDRESS_STEP: {
            return (
              <SoldToAddressStep
                onFieldValueChange={formProps.setFieldValue}
                onShipToAddressUpdate={setShipToAddress}
                isDefaultAddressSelected={isDefaultAddressSelected}
                billingProfileData={billingProfileData}
                customerAddresses={soldToAddresses}
                defaultSelectedAddress={shipToAddress}
                isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
              />
            );
          }
          // @ts-ignore
          case FormTrackerStep.CUSTOMER_CONTACT_STEP: {
            return (
              <>
                <ContactsTooltip type={ContactsTooltipType.CUSTOMER}>
                  <h3>Customer Contacts</h3>
                </ContactsTooltip>
                <HelperMessage>Manage the customer contacts of this billing profile.</HelperMessage>
                {isCustomerContactV3Enabled && (
                  <Field label="Primary customer contact" name="primary-customer-contact" isRequired>
                    {() => (
                      <AddContactFields
                        setAtlassianAccountIds={setPrimaryCustomerContactAccountIds}
                        atlassianAccountIds={primaryCustomerContactAccountIds}
                        atlassianAccountEmails={primaryCustomerContactAccountEmails}
                        setAtlassianAccountEmails={setPrimaryCustomerContactAccountEmails}
                        userAlreadyExistText="Customer contact already exists"
                        emailFieldText="Enter contact email"
                        nameFieldText="Enter contact full name"
                        addButtonText="Add customer contact"
                        contactFieldName="primary-customer-contact"
                        isAddNewContactDisabled
                        isExistingContactsBeEditable
                      />
                    )}
                  </Field>
                )}
                <Field
                  label={isCustomerContactV3Enabled ? 'Secondary customer contact(s)' : 'Customer contact(s)'}
                  name={isCustomerContactV3Enabled ? 'secondary-customer-contact' : 'customer-contact'}
                  isRequired={isCustomerContactV3Enabled ? false : true}
                >
                  {() => (
                    <AddContactFields
                      setAtlassianAccountIds={setSecondaryCustomerContactAccountIds}
                      atlassianAccountIds={secondaryCustomerContactAccountIds}
                      atlassianAccountEmails={secondaryCustomerContactAccountEmails}
                      setAtlassianAccountEmails={setSecondaryCustomerContactAccountEmails}
                      userAlreadyExistText="Customer contact already exists"
                      emailFieldText="Enter contact email"
                      nameFieldText="Enter contact full name"
                      addButtonText="Add customer contact"
                      isAddNewContactDisabled={isAddNewSecondaryContactDisabled}
                      isContactMandatory={isCustomerContactV3Enabled ? false : true}
                    />
                  )}
                </Field>
              </>
            );
          }

          // @ts-ignore
          case FormTrackerStep.BILLING_CONTACT_STEP: {
            return (
              <>
                <ContactsTooltip type={ContactsTooltipType.BILLING_CONTACT}>
                  <h3>Billing admins</h3>
                </ContactsTooltip>
                <HelperMessage>Manage the billing admins of this billing profile.</HelperMessage>
                <Field label="Billing admin(s)" name="billing-admin-contact">
                  {() => (
                    <AddContactSelectFields
                      setAtlassianAccountIds={setBillingContactAtlassianAccountIds}
                      atlassianAccountIds={billingContactAtlassianAccountIds}
                      atlassianAccountEmails={billingContactAtlassianAccountEmails}
                      setAtlassianAccountEmails={setBillingContactAtlassianAccountEmails}
                      userAlreadyExistText="Billing admin already exists"
                      emailFieldText="Select billing admin"
                      addButtonText="Add billing admin"
                      isExistingContactRemoveDisabled={false}
                      fieldType="billing-contact"
                      availableOptions={txaBillingAdmins.map((billingAdmin: BillingAdminEdge | null) => ({
                        fullName: billingAdmin?.node?.fullName ?? '',
                        email: billingAdmin?.node?.email ?? '',
                        isPartnerAdmin: billingAdmin?.node.isPartnerAdmin ?? false,
                        picture: billingAdmin?.node.picture ?? '',
                        atlassianAccountId: billingAdmin?.node.atlassianAccountId ?? '',
                      }))}
                      availableOptionsLoading={billingAdminsLoading}
                    />
                  )}
                </Field>
              </>
            );
          }

          // eslint-disable-next-line no-fallthrough
          case FormTrackerStep.PAYMENT_METHOD_STEP: {
            return (
              <PaymentMethodStep
                availablePaymentMethods={availablePaymentMethods}
                onFieldValueChange={formProps.setFieldValue}
                selectedPaymentId={billingProfileData?.defaultPaymentMethod}
                termPaymentStatus={termPaymentStatus}
                isPaypalSupportEnabled={isPaypalSupportEnabled}
                isTermPaymentSelectionDisabled={isTermPaymentSelectionDisabled}
                isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
              />
            );
          }
          default:
            return (
              <CurrencyPONumberStep
                isPONumberFeatureEnabled={isPONumberFeatureEnabled}
                isPOMultipleUseChecked={isPOMultipleUseChecked}
                onPOMultipleUseChangeEvent={onPOMultipleUseChangeEvent}
                purchaseOrderNumber={billingProfileData?.purchaseOrderNumber ?? ''}
                hideCurrency={!isPONumberFeatureEnabled} // hiding currency field as well if PO number feature is disabled
              />
            );
        }
      },
      [
        currentProgressIndex,
        transactionAccountId,
        isPONumberFeatureEnabled,
        isPOMultipleUseChecked,
        shipToAddress,
        billingProfileData,
        availablePaymentMethods,
        termPaymentStatus,
        transactionAccountData,
        primaryCustomerContactAccountIds,
        secondaryCustomerContactAccountIds,
        billingContactAtlassianAccountIds,
      ]
    );

    const items: Stages = useMemo(() => {
      return stages.map((stage, index) => ({
        id: `${index}`,
        label: stage,
        percentageComplete: currentProgressIndex - 1 < index ? 0 : 100,
        status:
          currentProgressIndex > index
            ? TrackerState.VISITED
            : currentProgressIndex === index
            ? TrackerState.CURRENT
            : TrackerState.UNVISITED,
        onClick: () => setCurrentProgressIndex(index),
      }));
    }, [currentProgressIndex]);

    const skipOrCancelForm = () => {
      closeSidebar();
    };

    const hasShipToAddressBeenSelected = isDefaultAddressSelected || Boolean(shipToAddress);
    const shouldDisableNextInCustomerContactStep =
      currentProgressIndex === FormTrackerStep.CUSTOMER_CONTACT_STEP &&
      ((isCustomerContactV3Enabled ? primaryCustomerContactAccountIds : secondaryCustomerContactAccountIds).length ===
        0 ||
        (isCustomerContactV3Enabled ? primaryCustomerContactAccountIds : secondaryCustomerContactAccountIds).findIndex(
          (id) => !id
        ) >= 0);

    return (
      <div className="billing-profile">
        {transactionAccountDataLoading ? (
          <LoadingIndicator />
        ) : (
          <Form onSubmit={submitCreateInvoiceGroupForm}>
            {({ formProps, submitting, dirty, ...props }) => (
              <form {...formProps} data-testid="create-billing-profile-form">
                <div className="header">
                  <FormHeader title="Create new billing profile">
                    <p aria-hidden="true">
                      Required fields are marked with an asterisk <RequiredAsterisk />
                    </p>
                  </FormHeader>
                </div>
                {!isProgressIndicatorEnabled && (
                  <div className="progress-content progress-parent-div">
                    <ProgressTracker items={items} spacing="compact" />
                  </div>
                )}
                <FormSection>
                  {currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP ? <h3>Billing profile name</h3> : null}
                  {error && (
                    <InfoPanel
                      type="error"
                      message={
                        <ErrorComponents.ErrorMessageOnInfoPanel
                          title="We're unable to create a new invoice group for the following reason"
                          message={error}
                          helpContent={<HelpContent />}
                        />
                      }
                    />
                  )}
                  {isNotValidAddress && (
                    <InfoPanel
                      type="error"
                      message={
                        <ErrorComponents.ErrorMessageOnInfoPanel
                          title="We're unable to create a new invoice group for the following reason"
                          message={getError(billingProfileData)}
                          helpContent={<HelpContent />}
                        />
                      }
                    />
                  )}
                </FormSection>
                {getFormStep(props)}
                {isProgressIndicatorEnabled ? (
                  <div className="form-footer" data-testId="progress-indicator-form-footer">
                    {' '}
                    <Button
                      isDisabled={currentProgressIndex === 0}
                      onClick={() => {
                        if (onBackCustomerClick && currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP) {
                          onBackCustomerClick();
                        }
                        setCurrentProgressIndex((prevState) => prevState - 1);
                      }}
                    >
                      Back
                    </Button>
                    <ProgressIndicator
                      selectedIndex={currentProgressIndex}
                      values={addCustomerFormStages ? addCustomerFormStages : defaultFormTrackerStep}
                      appearance="primary"
                      size="large"
                    />
                    <LoadingButton
                      appearance="primary"
                      type="submit"
                      isDisabled={
                        (currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP && !dirty) ||
                        !hasShipToAddressBeenSelected ||
                        shouldDisableNextInCustomerContactStep
                      }
                      isLoading={submitting || isProcessing || isSubmitLoading}
                      testId={submitting ? 'loading-button' : 'create-button'}
                    >
                      {currentProgressIndex === stages.length - 1 || currentProgressIndex === stages.length
                        ? 'Create'
                        : 'Next'}
                    </LoadingButton>
                  </div>
                ) : (
                  <FormFooter align="end">
                    <ButtonGroup>
                      <Button appearance="default" onClick={skipOrCancelForm} testId="cancel">
                        Cancel
                      </Button>
                      <LoadingButton
                        appearance="primary"
                        type="submit"
                        isDisabled={
                          (currentProgressIndex === FormTrackerStep.SOLD_TO_ADDRESS_STEP && !dirty) ||
                          !hasShipToAddressBeenSelected ||
                          shouldDisableNextInCustomerContactStep
                        }
                        isLoading={submitting || isProcessing}
                        testId={submitting ? 'loading-button' : 'create-button'}
                      >
                        {currentProgressIndex === stages.length - 1 || currentProgressIndex === stages.length
                          ? 'Create'
                          : 'Next'}
                      </LoadingButton>
                    </ButtonGroup>
                  </FormFooter>
                )}
              </form>
            )}
          </Form>
        )}
      </div>
    );
  }
);
